import { partition } from 'lodash';
import pRetry from 'p-retry';
import { NarrativePagination } from '@mark43/cad-api';
import { AdditionalInfoFormViewNext, AdditionalInfoType, AdditionalInfoView } from '../../../types';
import { ListDirection } from '../types';
import { useAdditionalInfosResource } from '../use-additional-infos-event-resource';
import { createStreamListener } from './helpers';
import { AdditionalInfosController } from './types';

const EVENT_NUMBER_REGEX = /(#[\w]{3,})/;

export interface CreateEventControllerConfig {
    entityId: number;
    resource: ReturnType<typeof useAdditionalInfosResource>;
    pageSize?: number;
    includeEventNarratives: boolean;
    /** When false (legacy endpoint), pagination wire shape is
     *  {hasNextPage, nextCursor} — translate to directional fields. */
    useNarrativeEndpoints: boolean;
    /** When false, the realtime infos.update handler skips reconciling the
     *  pinned section. Pinning is a Narrative-panel feature (Regular Event /
     *  Scheduled Event); message-source views such as ProQA must never show
     *  pinned entries, so leaving this off prevents a pinned narrative from
     *  being back-doored into the store via a realtime nudge. Opt-in: defaults
     *  to false. The hook always passes an explicit value derived from the
     *  provider prop, so this default is only a safety fallback. */
    enablePinnedEntries?: boolean;
}

// Translate legacy pagination shape ({hasNextPage, nextCursor}) to the
// directional shape the store + hook consume. The new endpoint already returns
// directional fields directly, so this is only for the legacy code path.
function translateLegacyPagination(
    raw: { hasNextPage?: boolean; nextCursor?: number } | undefined,
    listDirection: ListDirection | undefined
): NarrativePagination {
    if (!raw) return {};
    const hasNextPage = !!raw.hasNextPage;
    const cursor = raw.nextCursor ?? undefined;
    return listDirection === ListDirection.CATCHUP
        ? { newerCursor: cursor, hasNewerPage: hasNextPage }
        : { olderCursor: cursor, hasOlderPage: hasNextPage };
}

export function createEventController(
    config: CreateEventControllerConfig
): AdditionalInfosController {
    const {
        entityId,
        resource,
        pageSize,
        includeEventNarratives,
        useNarrativeEndpoints,
        enablePinnedEntries = false,
    } = config;

    return {
        async loadPage({ cursor, aroundId, listDirection, filter, agencyTypeGlobalAttrIds }) {
            return pRetry(
                async () => {
                    const response = await resource.getInfosByCursor({
                        agencyEventId: entityId,
                        limit: pageSize,
                        cursor,
                        aroundId,
                        direction: listDirection,
                        filter,
                        agencyTypeGlobalAttrIds,
                        includeEventNarratives,
                    });
                    const data = response?.data?.data;
                    if (!data) {
                        return {
                            additionalInfos: [],
                            pagination: {},
                            agencyEventNumberToIdMap: {},
                        };
                    }
                    const pagination: NarrativePagination = useNarrativeEndpoints
                        ? ((data.pagination as NarrativePagination) ?? {})
                        : translateLegacyPagination(
                              data.pagination as {
                                  hasNextPage?: boolean;
                                  nextCursor?: number;
                              },
                              listDirection
                          );
                    return {
                        // New endpoint returns CadNextAdditionalInfoView[]; legacy returns the
                        // ApiCadAdditionalInfo[] member of the union. Narrow to the view (the store's
                        // type) — the legacy path is deprecated (RND-61328) and reads only shared fields.
                        additionalInfos: (data.additionalInfos ?? []) as AdditionalInfoView[],
                        pagination,
                        agencyEventNumberToIdMap: data.agencyEventNumberToIdMap ?? {},
                    };
                },
                { retries: 2 }
            );
        },

        async createInfo(info: AdditionalInfoFormViewNext) {
            // currently additional-info creation endpoint will not return 'agencyEventNumberToIdMap'.
            // when a new additional-info is created, stream/nudge will indicate `infosToFetch`, then we call `fetchAdditionalInfosById` to get additional-infos with `agencyEventNumberToIdMap`
            // Once createAdditionalInfo return with 'agencyEventNumberToIdMap', we can call `upsertAgencyEventNumberToIdMap` immediately here
            const result = await resource.createAdditionalInfo(info);
            const view = result?.data?.data?.cadNextAdditionalInfoView as AdditionalInfoView;
            return view ? [view] : [];
        },

        async deleteInfo(infoId: number, agencyEventId?: number) {
            await resource.deleteInfo(agencyEventId ?? entityId, infoId);
        },

        async editInfo({ additionalInfoId, text }) {
            const result = await resource.editInfo({
                additionalInfoId,
                isScheduledEvent: false,
                text,
            });
            const partial = result.data.data;
            if (!partial) {
                throw new Error(`Edit narrative returned no data for id=${additionalInfoId}`);
            }
            return { id: partial.additionalInfoId, text: partial.text, priority: partial.priority };
        },

        async setPriority({ additionalInfoId, priority }) {
            const result = await resource.setPriority({
                additionalInfoId,
                isScheduledEvent: false,
                priority,
            });
            const partial = result.data.data;
            if (!partial) {
                throw new Error(`Emphasize narrative returned no data for id=${additionalInfoId}`);
            }
            return { id: partial.additionalInfoId, text: partial.text, priority: partial.priority };
        },

        async getPinnedNarratives() {
            const response = await resource.getPinnedNarratives({ agencyEventId: entityId });
            return (response?.data?.data?.pinnedNarratives ?? []) as AdditionalInfoView[];
        },

        async pinNarrative(infoId: number, agencyEventId?: number) {
            const response = await resource.pinNarrative({
                agencyEventId: agencyEventId ?? entityId,
                additionalInfoId: infoId,
            });
            const view = response?.data?.data?.additionalInfo as AdditionalInfoView | undefined;
            if (!view) {
                throw new Error(`Pin narrative returned no data for id=${infoId}`);
            }
            return view;
        },

        async unpinNarrative(infoId: number, agencyEventId?: number) {
            const response = await resource.unpinNarrative({
                agencyEventId: agencyEventId ?? entityId,
                additionalInfoId: infoId,
            });
            const view = response?.data?.data?.additionalInfo as AdditionalInfoView | undefined;
            if (!view) {
                throw new Error(`Unpin narrative returned no data for id=${infoId}`);
            }
            return view;
        },

        createStreamListeners({
            store,
            currentUserId,
            fetchNextPage,
            reloadPinnedNarratives,
            getLoadToken,
        }) {
            const shouldReloadAdditionalInfos = (events: number[] | undefined): boolean => {
                return Array.isArray(events) && events.length > 0 && events.includes(entityId);
            };

            const getAdditionalInfoIdsToEvict = (
                infos: { agencyEventId: number; id: number }[]
            ): number[] => {
                if (!Array.isArray(infos) || infos.length === 0) return [];
                return infos
                    .filter(
                        (info) =>
                            info.agencyEventId === entityId && store.listStore.hasItem(info.id)
                    )
                    .map((info) => info.id);
            };

            // Ids deleted on this event regardless of whether they're in the loaded
            // window — used to prune the pinned section, which lives outside the
            // window and so isn't covered by the window-gated eviction above.
            const getDeletedIdsForThisEvent = (
                infos: { agencyEventId: number; id: number }[]
            ): number[] => {
                if (!Array.isArray(infos) || infos.length === 0) return [];
                return infos
                    .filter((info) => info.agencyEventId === entityId)
                    .map((info) => info.id);
            };

            const matchesAgencyTypeFilter = (info: AdditionalInfoView): boolean => {
                if (!store.isAgencyTypeFilterActive) return true;
                if (info.type !== AdditionalInfoType.USER) return true;
                const originClass = info.originatedAgencyTypeGlobalAttrId;
                if (originClass == null) return true;
                return store.agencyTypeGlobalAttrIds.has(originClass);
            };

            const getAdditionalInfoIdsToInsert = (
                infos: AdditionalInfoView[],
                filterTypes: string[]
            ): AdditionalInfoView[] => {
                if (!Array.isArray(infos) || infos.length === 0) return [];
                return infos.filter((info) => {
                    if (store.listStore.getItemById(info.id)) return false;
                    const matchesAgencyEventId =
                        info.agencyEventId === entityId ||
                        (info.parentAgencyEventId && info.parentAgencyEventId === entityId);
                    const matchesFilter = filterTypes.includes(info.type);
                    return matchesAgencyEventId && matchesFilter && matchesAgencyTypeFilter(info);
                });
            };

            const fetchAdditionalInfosById = async (infos: AdditionalInfoView[]): Promise<void> => {
                const requestToken = getLoadToken();
                const response = await resource.getInfosByIds(entityId, {
                    ids: infos.map((info) => info.id),
                });
                if (useNarrativeEndpoints && requestToken !== getLoadToken()) {
                    return;
                }
                const data = response.data.data;
                if (data?.additionalInfos) {
                    store.upsertInfos(data.additionalInfos as AdditionalInfoView[], true);
                }
                if (data?.agencyEventNumberToIdMap) {
                    store.upsertAgencyEventNumberToIdMap(data.agencyEventNumberToIdMap);
                }
            };

            const infos = createStreamListener({
                streamNames: ['infos'],
                onMessage: (message) => {
                    if (shouldReloadAdditionalInfos(message.payload.reset)) {
                        fetchNextPage({ reset: true });
                        // A merge changes the pinned set (the surviving event gains the
                        // cleared event's pins; the cleared event's own section changes
                        // too) — the paginated reload above won't refresh the separate
                        // pinned list, so refetch it explicitly.
                        if (enablePinnedEntries) {
                            reloadPinnedNarratives();
                        }
                        return;
                    }

                    const deletedInfos = message.payload.delete ?? [];
                    const infoIdsToEvict = getAdditionalInfoIdsToEvict(deletedInfos);
                    if (infoIdsToEvict.length > 0) {
                        store.removeInfos(infoIdsToEvict);
                    }
                    // removeInfos prunes the pinned section for in-window ids; a pinned
                    // narrative scrolled out of the window isn't in that set, so prune it
                    // here. Only touch pinnedEntries when a deleted id actually matches one
                    // — pinnedEntries is always empty with the flag off, so this is a no-op
                    // there and the flag-off eviction path is unchanged.
                    if (enablePinnedEntries) {
                        const deletedIds = new Set(getDeletedIdsForThisEvent(deletedInfos));
                        if (store.pinnedEntries.some((entry) => deletedIds.has(entry.id))) {
                            deletedIds.forEach((id) => store.removePinnedEntry(id));
                        }
                    }

                    const infosToInsert = getAdditionalInfoIdsToInsert(
                        message.payload.create ?? [],
                        store.filter ?? []
                    ).filter((info) => !infoIdsToEvict.includes(info.id));

                    const infosToAdd = infosToInsert.filter((info) => info.text);
                    const infosToFetch = infosToInsert.filter(
                        (info) => !info.text || EVENT_NUMBER_REGEX.test(info.text)
                    );

                    if (infosToAdd.length > 0) {
                        const [ownInfos, otherInfos] = partition(
                            infosToAdd,
                            (info) => info.authorUserId === currentUserId
                        );
                        store.upsertInfos(otherInfos, true);
                        store.upsertInfos(ownInfos, false);
                    }

                    if (infosToFetch.length > 0) {
                        fetchAdditionalInfosById(infosToFetch);
                    }

                    const updates = message.payload.update ?? [];

                    // Only apply an update to the in-window row it actually belongs
                    // to on THIS panel. A pin/unpin (or any per-event) nudge for a
                    // linked cross-class event carries that event's agencyEventId; a
                    // shared narrative has the same id here, so matching on id alone
                    // would let the foreign nudge overwrite this row's agencyEventId
                    // and pin state — flipping it pinned on both events and dropping
                    // it out of the cross-class-filtered list until reload.
                    const infosToUpdate = updates.filter((info) => {
                        const existing = store.listStore.getItemById(info.id);
                        return (
                            existing !== undefined && existing.agencyEventId === info.agencyEventId
                        );
                    });
                    if (infosToUpdate.length > 0) {
                        store.upsertInfos(infosToUpdate);
                    }

                    // Reconcile the separate pinned section from the same
                    // infos.update nudge (pin/unpin publishes the re-mapped view
                    // via publishAdditionalInfoUpdatedNudge — the same nudge as
                    // edits, carrying updated isPinned/pinnedDateUtc). Pinned
                    // entries live outside the loaded window (fetched on
                    // bootstrap), so reconcile off the raw update payload rather
                    // than the in-window-filtered subset above.
                    //
                    // Pin state is PER-EVENT (it lives on additional_info_event_links).
                    // The BE scopes the pin nudge to the pinning event's agencyEventId,
                    // but for a cross-class narrative the nudge still reaches every
                    // linked event's subscriber — so a pin on the Police event would
                    // otherwise leak into the linked Fire event's pinned section. Only
                    // reconcile when the nudge's agencyEventId matches THIS event.
                    // addPinnedEntry drops any existing copy by id first, so a
                    // duplicate add is idempotent.
                    //
                    // Gated by enablePinnedEntries: message-source views (ProQA)
                    // opt out so a pin/unpin nudge for the underlying event can't
                    // back-door a pinned entry into a store that must never show
                    // the pinned section.
                    if (enablePinnedEntries) {
                        for (const info of updates) {
                            const pinnable = info as AdditionalInfoView;
                            if (pinnable.agencyEventId !== entityId) {
                                continue;
                            }
                            if (pinnable.isPinned) {
                                store.addPinnedEntry(pinnable);
                            } else {
                                store.removePinnedEntry(pinnable.id);
                            }
                        }
                    }
                },
            });

            return { infos };
        },

        createFilterDirective(crossClassEventsIds) {
            if (useNarrativeEndpoints) {
                return () => true;
            }
            // Legacy path only: when an FR user is reassigned to another event there
            // is a brief window where an in-flight load for the previously assigned
            // event upserts its rows into the shared store, mixing the old event's
            // narratives into the new event's list. Without the load-path guard
            // above, this whitelist (current event + its cross-class family) is the
            // only thing masking that stale-load window.
            const allRelevantEventsIds = [...crossClassEventsIds, entityId];
            return (item: AdditionalInfoView): boolean => {
                if (
                    item.parentAgencyEventId &&
                    allRelevantEventsIds.includes(item.parentAgencyEventId)
                ) {
                    return true;
                }
                return allRelevantEventsIds.includes(item.agencyEventId);
            };
        },
    };
}
